import { useState } from 'react';

import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { isNumber } from 'lodash-es';

import { useClientContext } from 'context/ClientContext';
import { usePedidoCompraContext } from 'context/PedidoCompraContext';
import {
    PedidoCompraAdicionarButton,
    PedidoCompraAmmount,
    PedidoCompraProdutoModal,
    PedidoCompraValue,
    PedidoDescontoChip,
    PedidoPrioritarioChip,
} from 'pages/compra/pedido/components';
import { ProdutoPedido } from 'pages/compra/pedido/types';
import { brlDecimal, camelCase } from 'util/format';

interface InfoContainerGridProps {
    value: React.ReactNode;
    subtitle: string;
    icon: React.ReactNode;
    opacity?: number;
}

/**
 * Componente para exibir informações do produto.
 */
function InfoContainerGrid({ value, subtitle, icon, opacity = 1 }: InfoContainerGridProps) {
    return (
        <Box sx={{ opacity }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1rem', letterSpacing: '-0.03rem' }}>{value}</Typography>

                {icon}
            </Box>

            <Typography sx={{ fontSize: '0.8rem', textAlign: 'right' }}>{subtitle}</Typography>
        </Box>
    );
}

export function PedidoCompraGridProduct({ produtoPedido }: { produtoPedido: ProdutoPedido }) {
    const { modifyProdListQtd, getProdListQtd, isProdutoDisabled, getProdMinQtd, configPedidoValidateQtdMin, getProdCart } = usePedidoCompraContext();
    const { produto, qtMinimaCompra, peOferta, fgShowPeOferta, fgObrigatorio } = produtoPedido;
    const produtoDisabled = isProdutoDisabled(produtoPedido);
    const produtoCart = getProdCart(produto.idProduto);
    const produtoQtd = produtoCart ? produtoCart.qtProdutoList : 0;
    const prodMinQtd = getProdMinQtd(qtMinimaCompra);
    const [openProdutoModal, setOpenProdutoModal] = useState<boolean>(false);

    const { tipoPedido } = useClientContext();
    const isTipoPedidoPadrao = tipoPedido === 'padrao';
    const isTipoPedidoFuncionario = tipoPedido === 'funcionario';
    const quantityTooltip =
        produtoDisabled || !configPedidoValidateQtdMin ? '' : `Quantidade deve ser múltipla de ${brlDecimal(prodMinQtd || 0, 0, 3)}`;

    return (
        <>
            <PedidoCompraProdutoModal open={openProdutoModal} setOpen={setOpenProdutoModal} produto={produtoPedido} />

            <Tooltip followCursor={true} title={produtoDisabled ? 'Produto indisponível para compra' : ''}>
                <Paper
                    variant="elevation"
                    elevation={2}
                    sx={{ width: '100%', height: '100%', opacity: produtoDisabled ? 0.6 : 1, p: 2, overflow: 'hidden', position: 'relative' }}
                >
                    {((!!peOferta && !!fgShowPeOferta) || fgObrigatorio) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                p: 1,
                                zIndex: 3,
                                top: 0,
                                right: 0,
                                left: 0,
                                display: 'flex',
                                justifyContent: !!peOferta && !!fgShowPeOferta ? 'space-between' : 'flex-end',
                            }}
                        >
                            {!!peOferta && !!fgShowPeOferta && <PedidoDescontoChip peOferta={peOferta} />}

                            {fgObrigatorio && <PedidoPrioritarioChip />}
                        </Box>
                    )}

                    <Stack sx={{ justifyContent: 'space-between', height: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                onClick={() => (isTipoPedidoFuncionario ? null : setOpenProdutoModal(true))}
                                sx={{
                                    height: 180,
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: 'contain',
                                    backgroundImage: `url("${produto.lkImagem}")`,
                                    mb: 2,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {!produto.lkImagem && <InsertPhotoOutlinedIcon sx={{ fontSize: 50, color: (theme) => theme.palette.grey[400] }} />}
                            </Box>

                            {isTipoPedidoPadrao && (
                                <IconButton
                                    onClick={() => setOpenProdutoModal(true)}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                    }}
                                >
                                    <InfoOutlinedIcon color="primary" />
                                </IconButton>
                            )}
                        </Box>

                        <Typography
                            onClick={() => (isTipoPedidoFuncionario ? null : setOpenProdutoModal(true))}
                            sx={{
                                fontSize: '1.1rem',
                                lineHeight: '1.3em',
                                overflow: 'hidden',
                                fontWeight: 500,
                                height: '2.8em',
                                cursor: 'pointer',
                            }}
                        >
                            {camelCase(produto.dsProduto, 2)}
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1.5fr 2fr',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body2">Valor Unitário</Typography>

                                <PedidoCompraValue value={produtoPedido.vlPrecoCompra} />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 0.5,
                                }}
                            >
                                {isTipoPedidoPadrao && (
                                    <>
                                        <InfoContainerGrid
                                            opacity={isNumber(produtoPedido.estoqueLoja?.qtSaldoAtual) ? 1 : 0.5}
                                            subtitle="Estoque na Loja"
                                            icon={<Inventory2OutlinedIcon />}
                                            value={
                                                <>
                                                    {brlDecimal(produtoPedido.estoqueLoja?.qtSaldoAtual || 0, 0, 3)}{' '}
                                                    {produto.unidadeMedida?.sgUnidadeMedida?.toLowerCase()}
                                                </>
                                            }
                                        />

                                        <InfoContainerGrid
                                            opacity={isNumber(produtoPedido.qtCoberturaEstoque) ? 1 : 0.5}
                                            subtitle="Cobertura de Estoque"
                                            icon={<DateRangeOutlinedIcon />}
                                            value={`${produtoPedido.qtCoberturaEstoque || 0} dias`}
                                        />

                                        <InfoContainerGrid
                                            opacity={isNumber(produtoPedido.qtSugerida) ? 1 : 0.5}
                                            subtitle="Quantidade Sugerida"
                                            icon={<CodeOutlinedIcon />}
                                            value={
                                                <>
                                                    {brlDecimal(produtoPedido.qtSugerida || 0, 0, 3)}{' '}
                                                    {produto.unidadeMedida?.sgUnidadeMedida?.toLowerCase()}
                                                </>
                                            }
                                        />
                                    </>
                                )}

                                <InfoContainerGrid
                                    opacity={isNumber(prodMinQtd) && configPedidoValidateQtdMin ? 1 : 0.5}
                                    subtitle="Quantidade Mínima"
                                    icon={<ShoppingCartOutlinedIcon />}
                                    value={
                                        <>
                                            {brlDecimal(prodMinQtd || 0, 0, 3)} {produto.unidadeMedida?.sgUnidadeMedida?.toLowerCase()}
                                        </>
                                    }
                                />

                                {isTipoPedidoFuncionario && (
                                    <InfoContainerGrid
                                        opacity={isNumber(produto.produtoEstoque?.qtEstoqueDisponivel) ? 1 : 0.5}
                                        subtitle="Quantidade Disponível"
                                        icon={<ShoppingCartOutlinedIcon />}
                                        value={
                                            <>
                                                {brlDecimal(
                                                    (produto.produtoEstoque?.qtEstoqueDisponivel > 0 &&
                                                        produto.produtoEstoque?.qtEstoqueDisponivel) ||
                                                        0,
                                                    0,
                                                    3,
                                                )}{' '}
                                                {produto.unidadeMedida?.sgUnidadeMedida?.toLowerCase()}
                                            </>
                                        }
                                    />
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title={quantityTooltip}>
                                <Box sx={{ width: 110 }}>
                                    <Typography variant="body2">Quantidade</Typography>

                                    <PedidoCompraAmmount getQtd={getProdListQtd} modifyQtd={modifyProdListQtd} produtoPedido={produtoPedido} />
                                </Box>
                            </Tooltip>

                            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                <PedidoCompraAdicionarButton
                                    idProduto={produto.idProduto}
                                    qtMinimaCompra={prodMinQtd}
                                    produtoQtd={produtoQtd}
                                    produtoCart={produtoCart}
                                    produtoDisabled={produtoDisabled}
                                />
                            </Box>

                            <Box
                                sx={{
                                    ml: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Typography variant="body2" align="right">
                                    Valor Total
                                </Typography>

                                <PedidoCompraValue value={produtoQtd > 0 ? produtoQtd * produtoPedido.vlPrecoCompra : 0} align="right" />
                            </Box>
                        </Box>
                    </Stack>
                </Paper>
            </Tooltip>
        </>
    );
}
